import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, Edit, Trash2, CreditCard } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";

export default function Vouchers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/vouchers")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load vouchers"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPdf = async (id) => {
    try {
      const res = await api.get(`/vouchers/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Failed to open PDF"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this voucher?")) return;
    await api.delete(`/vouchers/${id}`);
    toast.success("Deleted");
    load();
  };

  const leadName = (v) => (v.passengers || []).find((p) => (p?.name || "").trim())?.name || "—";

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((v) => {
        const blob = [
          v.voucher_number, v.hotel_name, v.hotel_city, v.hotel_country,
          v.pnr, v.reference_id, v.supplier_confirmation_no,
          ...(v.passengers || []).map((p) => p?.name || ""),
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(q);
      })
    : items;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline text-[10px] tracking-[0.28em] text-emerald-700">HOTEL VOUCHERS</div>
            <h1 className="font-editorial text-3xl text-slate-900 mt-1">Confirmed hotel bookings</h1>
            <p className="text-sm text-slate-500 mt-1">Generate a premium hotel voucher PDF for any confirmed booking.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="voucher-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by guest, city, hotel, PNR…"
              className="w-64 border border-border rounded px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <button
              data-testid="voucher-new-btn"
              onClick={() => nav("/vouchers/new")}
              className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-4 py-2.5 rounded text-sm font-semibold"
            >
              <Plus size={15} /> New Voucher
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center">
            <CreditCard size={32} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500 mb-4">No hotel vouchers yet. Create your first one to see it here.</div>
            <button
              data-testid="voucher-first-btn"
              onClick={() => nav("/vouchers/new")}
              className="inline-flex items-center gap-2 text-sm text-[#16A34A] hover:underline"
            >
              <Plus size={14} /> Create hotel voucher
            </button>
          </div>
        ) : (
          <div className="bg-white border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">Voucher #</th>
                  <th className="text-left px-4 py-3">Lead Guest</th>
                  <th className="text-left px-4 py-3">Hotel</th>
                  <th className="text-left px-4 py-3">City</th>
                  <th className="text-left px-4 py-3">PNR</th>
                  <th className="text-left px-4 py-3">Check-in</th>
                  <th className="text-left px-4 py-3">Nights</th>
                  <th className="text-left px-4 py-3">Guests</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id} data-testid={`voucher-row-${v.id}`} className="border-t border-border hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{v.voucher_number}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{leadName(v)}</td>
                    <td className="px-4 py-3 text-slate-700">{v.hotel_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.hotel_city || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.pnr || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.check_in_date || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.nights}</td>
                    <td className="px-4 py-3 text-slate-600">{(v.passengers || []).length}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                        {v.status || "CONFIRMED"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`voucher-pdf-${v.id}`} onClick={() => openPdf(v.id)} className="p-1.5 text-slate-500 hover:text-[#16A34A]" title="Download PDF"><FileDown size={15} /></button>
                        <Link data-testid={`voucher-edit-${v.id}`} to={`/vouchers/${v.id}/edit`} className="p-1.5 text-slate-500 hover:text-blue-600" title="Edit"><Edit size={15} /></Link>
                        <button data-testid={`voucher-del-${v.id}`} onClick={() => remove(v.id)} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && q && (
                  <tr>
                    <td colSpan={10} className="text-center text-slate-400 text-sm py-6">
                      No vouchers match "<b>{search}</b>". Try a different search term.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
